import { EditableActivity } from "@ac-expo/api-types";
import { toDatabaseFormat } from "@ac-expo/utils";
import { Activity } from "@prisma/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../../axiosInstances";
import { eventsQueryKeys } from "../../query-keys";
import { activitiesQueryKeys } from "../../query-keys/activities.query-keys";
import { CreateTicketTypeForm, UpsertActivityForm } from "../../schemas";

const upsertActivity = async ({
  data,
  eventId,
}: {
  data: UpsertActivityForm;
  eventId: string;
}): Promise<Activity> => {
  if (!data.id) {
    const { data: response } = await privateAxios.post(
      `/activities/event/${eventId}${!data.structureId ? "/organiser" : ""}`,
      data,
    );
    return response;
  }
  const { data: response } = await privateAxios.patch(`/activities/${data.id}`, data);
  return response;
};

export const useUpsertActivityMutation = (eventId: string, structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upsertActivity,
    onSuccess: (_, { data: { id } }) => {
      queryClient.invalidateQueries({
        queryKey: activitiesQueryKeys.event(eventId, structureId),
      });
      if (id)
        queryClient.invalidateQueries({
          queryKey: activitiesQueryKeys.get(id),
        });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const deleteActivity = async (id: string) => {
  await privateAxios.delete(`/activities/${id}`);
};

export const useDeleteActivityMutation = (eventId: string, structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await deleteActivity(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: activitiesQueryKeys.event(eventId, structureId),
      });
    },
  });
};

const toggleActivityOnline = async (id: string, value?: boolean) => {
  const { data: response } = await privateAxios.patch(`/activities/${id}/online`, {
    isOnline: value,
  });

  return response;
};

export const useToggleActivityOnlineMutation = (id: string, isStructure?: boolean) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (value?: boolean) => toggleActivityOnline(id, value),
    onSuccess: (data: EditableActivity) => {
      const structureId = isStructure ? (data.structureId ?? undefined) : undefined;

      queryClient.invalidateQueries({
        queryKey: activitiesQueryKeys.event(data.eventId, structureId),
      });
      queryClient.invalidateQueries({
        queryKey: activitiesQueryKeys.get(id),
      });
    },
  });
};

export type UpsertActivityTicketData = {
  quantity: number | null;
  isQuantityInfinite: boolean;
} & Omit<CreateTicketTypeForm, "quantity">;

const getDataFromFormData = (data: UpsertActivityTicketData) => ({
  ...data,
  validityDates: data.validityDates.map(toDatabaseFormat),
  discountCodes: data.discountCodes.map((code) => ({
    ...code,
    validityDates: code.validityDates.map(toDatabaseFormat),
  })),
});

const upsertActivityTicket = async (activityId: string, data: UpsertActivityTicketData) => {
  if (data.id) {
    const { data: response } = await privateAxios.patch(
      `/activities/${activityId}/tickets/${data.id}`,
      getDataFromFormData(data),
    );
    return response;
  }

  const { data: response } = await privateAxios.post(
    `/activities/${activityId}/tickets`,
    getDataFromFormData(data),
  );
  return response;
};

export const useUpsertActivityTicketMutation = (activityId: string, eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertActivityTicketData) => upsertActivityTicket(activityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: activitiesQueryKeys.get(activityId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const linkBankDetailsToEvent = async (eventId: string, bankDetailsId: string) => {
  await privateAxios.post(`/events/add-bank-details/${eventId}`, {
    bankDetailsId,
  });
};

export const useLinkBankDetailsToEventMutation = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bankDetailsId: string) => linkBankDetailsToEvent(id, bankDetailsId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(id),
      });
    },
  });
};
